// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '../../providers';
import type { AccountTransaction } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { useCallback, useMemo, useState } from 'react';
import { usePeriodPicker } from '../../hooks/use-period-picker';
import { type LoaderStatus } from '../loader';
import {
  exportTableToExcel,
  HistValues,
  useTableTextSearchFilter,
} from '@shared/ui-kit';
import { useTranslation } from 'react-i18next';
import { useAccountsTransactions } from '../../hooks';

export const useTransactions = () => {
  const { t } = useTranslation();
  const {
    institutionId,
    clientId,
    selectedAccounts,
    accountOptions,
    onChangeAccounts,
    updatedDate,
    onRefresh: onRefreshAggregation,
    currency,
    shouldRefresh,
    refreshMode,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
  } = useAggregationData();

  const periodPicker = usePeriodPicker(HistValues['1Y']);

  const { accountsTransactionsQuery, ...accountsTransactions } =
    useAccountsTransactions({
      clientId,
      ibans: selectedAccounts,
      start_date: periodPicker.start_date,
      institutionId,
      currency,
    });

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = [accountsTransactionsQuery.isLoading].some(Boolean);

    const isError = [accountsTransactionsQuery.isError].some(Boolean);

    const isFetching = [accountsTransactionsQuery.isFetching].some(Boolean);

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    accountsTransactionsQuery.isError,
    accountsTransactionsQuery.isFetching,
    accountsTransactionsQuery.isLoading,
  ]);

  const {
    filteredData: transactions,
    textSearch,
    handleTextSearch,
  } = useTableTextSearchFilter({
    data: accountsTransactions.transactions,
    searchFields: [
      'date',
      'subtype',
      'currency',
      'ticker',
      'isin',
      'name',
      'description',
      'type',
      'asset_type',
    ],
    sortField: 'date',
  });

  const onDownloadTransactions = useCallback(() => {
    exportTableToExcel(transactions, `Transactions ${periodPicker.start_date}`);
  }, [periodPicker.start_date, transactions]);

  const hasData = useMemo(
    () => accountsTransactions.transactions.length > 0,
    [accountsTransactions.transactions.length],
  );

  const onRefresh = useCallback(() => {
    if (shouldRefresh) {
      accountsTransactionsQuery.refetch();

      return;
    }

    onRefreshAggregation();
  }, [shouldRefresh, onRefreshAggregation, accountsTransactionsQuery]);

  const [selectedTransaction, setSelectedTransaction] =
    useState<AccountTransaction | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const openTransactionDrawer = useCallback((transaction: AccountTransaction) => {
    setSelectedTransaction(transaction);

    setDrawerOpen(true);
  }, []);

  const onDrawerClose = useCallback(() => {
    setDrawerOpen(false);

    setSelectedTransaction(null);
  }, []);

  const onDrawerOpen = useCallback(() => setDrawerOpen(true), []);

  return {
    transactions,
    onDownloadTransactions,
    handleTextSearch,
    textSearch,
    onRefresh,
    status,
    hasData,
    t,
    institutionId,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    periodPicker,
    updatedDate,
    refreshMode,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    selectedTransaction,
    drawerOpen,
    openTransactionDrawer,
    onDrawerClose,
    onDrawerOpen,
  } as const;
};
